import type { Reference, Patient } from '@medplum/fhirtypes'
import { featureFlags } from '@seen/core/featureFlags'
import type { NoteTemplate } from '@seen/core/models/noteTemplate'
import { ExtendedObservation } from '@seen/core/models/observation'
import type { Participant } from '@seen/core/models/patients/participant'
import { instrumentGroupQuery } from '@seen/core/observation-logic/instruments/records'
import { referenceStringToId } from '@seen/core/references'
import { useQueryClient, type DefaultError } from '@tanstack/react-query'
import Placeholder from '@tiptap/extension-placeholder'
import type { PlaceholderOptions } from '@tiptap/extension-placeholder'
import type { Extension, Extensions } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { compact, uniq, uniqBy } from 'lodash-es'
import { useCallback, useMemo, useRef } from 'react'
import { toast } from 'sonner'
import { AIProposalsExtension } from '#src/components/rich-text-editor/AIProposalsExtension.ts'
import { ChoiceNode } from '#src/components/rich-text-editor/choice/ChoiceNode.tsx'
import { ConditionalDisplayExtension } from '#src/components/rich-text-editor/conditional-display/ConditionalDisplayExtension.ts'
import { ConditionalSectionNode } from '#src/components/rich-text-editor/conditional-display/ConditionalSectionNode.ts'
import { ConditionalSpanNode } from '#src/components/rich-text-editor/conditional-display/ConditionalSpanNode.ts'
import { EncounterMedicationsBlockNode } from '#src/components/rich-text-editor/EncounterMedicationsBlockNode/EncounterMedicationsBlockNode.tsx'
import {
  getEditorHelpers,
  getRenderExtensions,
  STARTER_KIT_OPTIONS,
} from '#src/components/rich-text-editor/extensions.ts'
import { MentionNode } from '#src/components/rich-text-editor/mention/MentionNode.tsx'
import type { InputRemovedRecords } from '#src/components/rich-text-editor/ObservationNode/observationInputRemoval.ts'
import { ObservationNode } from '#src/components/rich-text-editor/ObservationNode/ObservationNode.tsx'
import { ObservationPanelNode } from '#src/components/rich-text-editor/ObservationNode/panel/ObservationPanelNode.ts'
import type {
  ObservationRecordStatus,
  RecordedObservationNode,
} from '#src/components/rich-text-editor/ObservationNode/types.ts'
import { useRemoveObservationRecord } from '#src/components/rich-text-editor/ObservationNode/useRemoveObservationRecord.ts'
import { RegistryVariableNode } from '#src/components/rich-text-editor/registry-variable/RegistryVariableNode.ts'
import { RequiredAnswersExtension } from '#src/components/rich-text-editor/required/RequiredAnswersExtension.ts'
import { createSuggestionWidgetShells } from '#src/components/rich-text-editor/suggestionWidgetShells.ts'
import { TemplateVariableNode } from '#src/components/rich-text-editor/template-variable/TemplateVariableNode.tsx'
import { useDebouncedCallback } from '#src/hooks/useDebouncedCallback.ts'
import { useDeleteConfirmationStore } from '#src/hooks/useDeleteConfirmationStore.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useTransactionMutation } from '#src/hooks/useFhirBundleMutation.ts'
import { useFileHandler } from '#src/hooks/useFileHandler.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { useMemoValue } from '#src/hooks/useMemoValue.ts'
import { searchOptions } from '#src/query-options/searchOptions.ts'

interface ConfigureOpts {
  inlineChoiceSources: boolean
  disableStarterKitHistory?: boolean
  contextParticipant?: Participant
  choiceNodesEditable?: boolean
  enablePractitionerMention?: boolean
  enableOrderTemplateMention?: boolean
  isTemplate?: boolean
  structuredCapture: boolean
  onObservationsChanged?: (payload: {
    added: string[]
    removed: string[]
  }) => void
  onObservationReferencePersisted?: () => void
  onConfirmObservationRemove?: (
    records: RecordedObservationNode[]
  ) => Promise<boolean>
  onRemoveObservationRecords?: (removed: InputRemovedRecords) => void
  observationRecordStatus: ObservationRecordStatus
  onMergeTemplateMetadata?: (template: NoteTemplate) => void
}

/* Substitute runtime-configured editor extensions in place of their render-safe
 * defaults from RENDER_EXTENSIONS. Anything not in the switch passes through
 * unchanged (Typography, TaskList, button nodes, etc. — none need
 * editor-only runtime config). */
function configureForEditor(
  ext: Extensions[number],
  opts: ConfigureOpts
): Extensions[number] {
  switch (ext.name) {
    case 'aiProposals':
      /* Inject the host-owned shell manager so pending insert content previews
       * through the real node views (design §1.2). A React host is present here
       * by construction (this runs inside a React hook), so the mounter's
       * ReactRenderer portals resolve against the editor's contentComponent. */
      return AIProposalsExtension.configure({
        createShells: createSuggestionWidgetShells,
        inlineChoiceSources: opts.inlineChoiceSources,
      })
    case 'starterKit':
      return StarterKit.configure({
        ...STARTER_KIT_OPTIONS,
        undoRedo: opts.disableStarterKitHistory ? false : undefined,
      })
    case 'templateVariable':
      // No participant: {{token}} text becomes a registryVariable node
      // rather than being expanded into plain text. Only the `{{` picker in
      // templates is used, and the text it inserts is converted the same way.
      return TemplateVariableNode.configure({
        isTemplate: opts.isTemplate,
        enabled: opts.structuredCapture,
      })
    case 'registryVariable':
      return RegistryVariableNode.configure({
        isTemplate: Boolean(opts.isTemplate),
      })
    case 'choice':
      return ChoiceNode.configure({
        editable: opts.choiceNodesEditable,
      })
    case 'mention':
      return MentionNode.configure({
        enablePractitionerMention: opts.enablePractitionerMention,
        enableOrderTemplateMention: opts.enableOrderTemplateMention,
        isTemplate: opts.isTemplate,
        onMergeTemplateMetadata: opts.onMergeTemplateMetadata,
      })
    case 'encounter-medications-block':
      return EncounterMedicationsBlockNode.configure({
        patientReferenceString: opts.contextParticipant?.reference.reference,
      })
    case 'observation-panel':
      return ObservationPanelNode.configure({
        participant: opts.contextParticipant?.reference,
        recordStatus: opts.observationRecordStatus,
        isTemplate: Boolean(opts.isTemplate),
      })
    case 'conditionalSection':
      return ConditionalSectionNode.configure({
        isTemplate: Boolean(opts.isTemplate),
      })
    case 'conditionalSpan':
      return ConditionalSpanNode.configure({
        isTemplate: Boolean(opts.isTemplate),
      })
    case 'observation':
      return ObservationNode.configure({
        participant: opts.contextParticipant?.reference,
        onObservationsChanged: opts.onObservationsChanged,
        onReferencePersisted: opts.onObservationReferencePersisted,
        onConfirmRemove: opts.onConfirmObservationRemove,
        onRemoveRecords: opts.onRemoveObservationRecords,
        recordStatus: opts.observationRecordStatus,
        isTemplate: Boolean(opts.isTemplate),
      })
    default:
      return ext
  }
}

interface UseTiptapExtensionsResult {
  extensions: Extensions
  cancelObservationTracking: () => void
}

export function useTiptapExtensions({
  enableFileHandler = true,
  placeholder,
  fileHandlerDocumentReferenceContextRelated,
  contextParticipant,
  choiceNodesEditable,
  enablePractitionerMention,
  enableOrderTemplateMention,
  disableStarterKitHistory,
  disableMention,
  isTemplate,
  enableColumns,
  liveblocksProvidesCommentMark = false,
  onObservationReferencePersisted,
  onMergeTemplateMetadata,
  observationRecordStatus = 'final',
  editorParticipant,
}: {
  enableFileHandler?: boolean
  placeholder?: PlaceholderOptions['placeholder']
  fileHandlerDocumentReferenceContextRelated?: Reference[]
  contextParticipant?: Participant
  choiceNodesEditable?: boolean
  enablePractitionerMention?: boolean
  enableOrderTemplateMention?: boolean
  disableStarterKitHistory?: boolean
  disableMention?: boolean
  isTemplate?: boolean
  enableColumns?: boolean
  /* The host already composes @liveblocks/react-tiptap, whose mark includes
   * thread UI; omit the core render-only copy to avoid a duplicate name. */
  liveblocksProvidesCommentMark?: boolean
  onObservationReferencePersisted?: () => void
  onMergeTemplateMetadata?: (template: NoteTemplate) => void
  // Visit notes pass `preliminary`; signing finalizes the records.
  observationRecordStatus?: ObservationRecordStatus
  /* The participant the editor gives `EditorParticipantContext`, which nodes
   * follow as it is assigned or switched after the editor is built. `null`:
   * none chosen. Omitted: `contextParticipant`. */
  editorParticipant?: Reference<Patient> | null
}): UseTiptapExtensionsResult {
  const fileHandler = useFileHandler(fileHandlerDocumentReferenceContextRelated)
  /* Part of the memo deps, but the plugin reads it once when the editor is
   * built (see the extension's option), so a value that changed after mount
   * would not reach it. The flag snapshot is fixed before any editor mounts —
   * the portal holds its children until the values resolve — which is what
   * keeps that from mattering. */
  const inlineChoiceSources = useFeatureFlag(
    featureFlags.aiChatChoiceInlineSource
  )

  const { onObservationsChanged, cancelObservationTracking } =
    useHandleObservationsChanged()
  const confirmObservationRemove = useConfirmObservationRemove()
  const removeObservationRecords = useRemoveObservationRecords()
  const clearHiddenAnswer = useCallback(
    (references: string[]) =>
      removeObservationRecords(references, { preliminaryOnly: false }),
    [removeObservationRecords]
  )
  /* With structured capture on, removing a recorded node is an explicit,
   * confirmed action, so the document diff no longer writes record status.
   * Off, the diff-driven sync stays the only path, and records stay final
   * because nothing else would finalize or retract them. */
  const structuredCapture = useFeatureFlag(featureFlags.structuredCapture)
  const effectiveRecordStatus = structuredCapture
    ? observationRecordStatus
    : 'final'

  /* `contextParticipant` is a model instance re-created on every React Query
   * cache write (see cacheIndividualResources), so its identity churns even when
   * the underlying participant is unchanged. Feeding that identity into the
   * extensions memo rebuilds the array on every cache event, and @tiptap/react
   * then re-runs editor.setOptions() to swap the whole extension set — a steady
   * heap/CPU drain on the visit-note page. Collapse it to a stable reference
   * keyed on the participant's data; the participant-aware nodes
   * (encounter-medications, observation) only read its
   * content, never its identity. */
  const stableContextParticipant = useMemoValue(contextParticipant)
  // Read when a removal runs, not when the editor's plugins are built, so a
  // participant assigned or switched later is the one searched.
  const liveParticipantRef = useRef<string | undefined>(undefined)
  liveParticipantRef.current =
    editorParticipant === undefined
      ? stableContextParticipant?.reference.reference
      : (editorParticipant?.reference ?? undefined)
  // Nodes and panels typed or pasted over leave only preliminary records to
  // take back; a final one belongs to a signed note this editor cannot change.
  // A panel's group is found under its participant, so without one there is
  // no group to remove.
  const removeInputRecords = useCallback(
    ({ references, groupIds }: InputRemovedRecords) => {
      const participant = liveParticipantRef.current
      void removeObservationRecords(references, {
        preliminaryOnly: true,
        groups:
          participant && groupIds.length
            ? { subject: { reference: participant }, groupIds }
            : undefined,
      }).catch(() => {})
    },
    [removeObservationRecords]
  )

  const extensions = useMemo(() => {
    const configured = getRenderExtensions()
      .filter((ext) => {
        if (disableMention && ext.name === 'mention') {
          return false
        }
        if (
          !enableColumns &&
          (ext.name === 'columnBlock' || ext.name === 'column')
        ) {
          return false
        }
        if (
          liveblocksProvidesCommentMark &&
          ext.name === 'liveblocksCommentMark'
        ) {
          return false
        }
        return true
      })
      .map((ext) =>
        configureForEditor(ext, {
          inlineChoiceSources,
          disableStarterKitHistory,
          contextParticipant: stableContextParticipant,
          choiceNodesEditable,
          enablePractitionerMention,
          enableOrderTemplateMention,
          isTemplate,
          structuredCapture,
          onObservationsChanged: structuredCapture
            ? undefined
            : onObservationsChanged,
          onConfirmObservationRemove: structuredCapture
            ? confirmObservationRemove
            : undefined,
          onRemoveObservationRecords: structuredCapture
            ? removeInputRecords
            : undefined,
          observationRecordStatus: effectiveRecordStatus,
          onObservationReferencePersisted,
          onMergeTemplateMetadata,
        })
      )

    const editorHelpers = getEditorHelpers().map((ext) => {
      if (ext.name === 'conditionalDisplay') {
        return ConditionalDisplayExtension.configure({
          enabled: structuredCapture,
          isTemplate: Boolean(isTemplate),
          removeRecords: clearHiddenAnswer,
        })
      }
      if (ext.name === 'requiredAnswers') {
        return RequiredAnswersExtension.configure({
          enabled: structuredCapture,
          isTemplate: Boolean(isTemplate),
        })
      }
      if (ext.name === 'placeholder') {
        return Placeholder.configure({
          placeholder,
        }) as Extension<PlaceholderOptions>
      }
      return ext
    })

    return compact([
      ...configured,
      enableFileHandler ? fileHandler : undefined,
      ...editorHelpers,
    ])
  }, [
    placeholder,
    enableFileHandler,
    fileHandler,
    disableStarterKitHistory,
    stableContextParticipant,
    choiceNodesEditable,
    enablePractitionerMention,
    enableOrderTemplateMention,
    disableMention,
    isTemplate,
    enableColumns,
    onObservationsChanged,
    structuredCapture,
    confirmObservationRemove,
    removeInputRecords,
    clearHiddenAnswer,
    effectiveRecordStatus,
    onObservationReferencePersisted,
    onMergeTemplateMetadata,
    liveblocksProvidesCommentMark,
    inlineChoiceSources,
  ])
  return { extensions, cancelObservationTracking }
}

const OBSERVATIONS_CHANGED_DEBOUNCE_MS = 300

interface UseHandleObservationsChangedResult {
  onObservationsChanged: (payload: {
    added: string[]
    removed: string[]
  }) => void
  cancelObservationTracking: () => void
}

function useHandleObservationsChanged(): UseHandleObservationsChangedResult {
  const queryClient = useQueryClient()
  const medplum = useMedplum()
  const transactionMutateAsync = useTransactionMutation().mutateAsync

  const pendingAdded = useRef<Set<string>>(new Set())
  const pendingRemoved = useRef<Set<string>>(new Set())
  const ignoreChanges = useRef(false)

  const syncObservations = useDebouncedCallback(async () => {
    const added = [...pendingAdded.current]
    const removed = [...pendingRemoved.current]
    pendingAdded.current = new Set()
    pendingRemoved.current = new Set()

    if (!added.length && !removed.length) {
      return
    }

    const referenceStrings = uniq([...added, ...removed])
    if (!referenceStrings.length) {
      return
    }

    let observations: ExtendedObservation[] = []

    try {
      observations = await queryClient.fetchQuery(
        searchOptions(medplum, ExtendedObservation, {
          _id: referenceStrings.map(referenceStringToId),
        })
      )
    } catch (error) {
      toast.error(
        `Failed to fetch observations for status update${error instanceof Error ? `: ${error.message}` : ''}`
      )
      throw error
    }

    const removedSet = new Set(removed)
    const patches = observations
      .map((observation) => {
        const desiredStatus = removedSet.has(observation.reference.reference)
          ? ('entered-in-error' as const)
          : ('final' as const)

        if (observation.status === desiredStatus) {
          // Already in the correct state → no-op
          return null
        }

        return { observation, desiredStatus }
      })
      .filter((x) => x !== null)

    if (!patches.length) {
      return
    }

    try {
      await transactionMutateAsync(async (txn) => {
        patches.forEach(({ observation, desiredStatus }) => {
          txn.patch(observation, (o) => {
            o.status = desiredStatus
          })
        })
      })
    } catch (error) {
      toast.error(
        `Failed to update observation statuses${error instanceof Error ? `: ${error.message}` : ''}`
      )
      throw error
    }
  }, OBSERVATIONS_CHANGED_DEBOUNCE_MS)

  const cancelObservationTracking = useCallback(() => {
    // Set flag to ignore all future observation changes
    ignoreChanges.current = true
    // Cancel the debounced sync
    syncObservations.cancel()
    // Clear pending changes
    pendingAdded.current = new Set()
    pendingRemoved.current = new Set()
  }, [syncObservations])

  const onObservationsChanged = useCallback(
    ({ added, removed }: { added: string[]; removed: string[] }) => {
      // If ignoreChanges flag is set, only ignore removals (not additions)
      // When user starts composing a new note and adds observations, we want to track them
      if (ignoreChanges.current) {
        if (added.length > 0) {
          // User is adding new observations, re-enable tracking
          ignoreChanges.current = false
        } else if (removed.length > 0) {
          // Still in ignore mode, skip removals
          return
        }
      }

      for (const ref of added) {
        pendingRemoved.current.delete(ref)
        pendingAdded.current.add(ref)
      }
      for (const ref of removed) {
        pendingAdded.current.delete(ref)
        pendingRemoved.current.add(ref)
      }

      void syncObservations()
    },
    [syncObservations]
  )

  return { onObservationsChanged, cancelObservationTracking }
}

/* Looks the records up, with every record of each instrument group in
 * `groups`, and removes each through `useRemoveObservationRecord` (a
 * preliminary one deleted, anything else entered-in-error). With
 * `preliminaryOnly`, a record that is no longer preliminary is left alone. */
function useRemoveObservationRecords(): (
  references: string[],
  options: {
    preliminaryOnly: boolean
    groups?: { subject: Reference<Patient>; groupIds: string[] }
  }
) => Promise<void> {
  const queryClient = useQueryClient()
  const medplum = useMedplum()
  const removeRecord = useRemoveObservationRecord()
  return useCallback(
    async (references, { preliminaryOnly, groups }) => {
      const ids = uniq(references.map(referenceStringToId))
      if (!ids.length && !groups) {
        return
      }
      const search = (query: Record<string, string | string[]>) =>
        queryClient.fetchQuery({
          ...searchOptions(medplum, ExtendedObservation, query),
          staleTime: 0,
        })
      try {
        const found = await Promise.all([
          ids.length ? search({ _id: ids }) : [],
          groups ? search(instrumentGroupQuery(groups)) : [],
        ])
        await Promise.all(
          uniqBy(found.flat(), (observation) => observation.id)
            .filter(
              (observation) => !preliminaryOnly || observation.isPreliminary
            )
            .map(removeRecord)
        )
      } catch (error) {
        toast.error(
          `Failed to remove observations${error instanceof Error ? `: ${error.message}` : ''}`
        )
        throw error
      }
    },
    [queryClient, medplum, removeRecord]
  )
}

function removalTitle(records: RecordedObservationNode[]): string {
  return records.length === 1
    ? `Remove "${records[0].label}"?`
    : `Remove ${records.length} observations?`
}

/* Opens the shared confirmation and resolves once the user answers: `true`
 * only after every record is removed, so the node never leaves the note while
 * its record is still on the chart. Closing the dialog, or another
 * confirmation taking it over, resolves `false`. */
function useConfirmObservationRemove(): (
  records: RecordedObservationNode[]
) => Promise<boolean> {
  const queryClient = useQueryClient()
  const medplum = useMedplum()
  const removeRecord = useRemoveObservationRecord()

  return useCallback(
    (records: RecordedObservationNode[]) =>
      new Promise<boolean>((resolve) => {
        let settled = false
        let confirmed = false
        const settle = (value: boolean): void => {
          if (settled) {
            return
          }
          settled = true
          unsubscribe()
          resolve(value)
        }
        const onConfirm = (): void => {
          confirmed = true
          const ids = uniq(records.map((r) => referenceStringToId(r.reference)))
          queryClient
            .fetchQuery(
              searchOptions(medplum, ExtendedObservation, { _id: ids })
            )
            .then((observations) => Promise.all(observations.map(removeRecord)))
            .then(() => settle(true))
            .catch((error: DefaultError) => {
              toast.error(
                `Failed to remove ${records.length === 1 ? records[0].label : 'observations'}${error instanceof Error ? `: ${error.message}` : ''}`
              )
              settle(false)
            })
        }
        useDeleteConfirmationStore.getState().open({
          title: removalTitle(records),
          description:
            'The recorded value is removed from the chart. It can still be recovered by contacting our team.',
          deleteText: 'Remove',
          onConfirm,
        })
        const unsubscribe = useDeleteConfirmationStore.subscribe((state) => {
          if (confirmed) {
            return
          }
          if (!state.isOpen || state.onConfirm !== onConfirm) {
            settle(false)
          }
        })
      }),
    [queryClient, medplum, removeRecord]
  )
}
